import type { z } from 'zod';
import type { SiteSchema } from './model';
import { incidentProfiles, defaultProfiles, type ProfileId } from './incidentProfiles';
import type { Incident, Point, SimState, Station, Vehicle } from './model';
import { defaultArea } from './regions';
export const locations: { address: string; district: string; location: Point }[] = [
  { address: 'Rotebühlstraße 120', district: 'Stuttgart-West', location: [48.7699, 9.1578] },
  {
    address: 'Heilbronner Straße / Türlenstraße',
    district: 'Stuttgart-Nord',
    location: [48.7926, 9.1808],
  },
  { address: 'Neckarstraße 150', district: 'Stuttgart-Ost', location: [48.7902, 9.2023] },
  { address: 'Hauptstätter Straße 80', district: 'Stuttgart-Süd', location: [48.7686, 9.1767] },
  { address: 'Mercedesstraße 50', district: 'Bad Cannstatt', location: [48.7932, 9.2219] },
  { address: 'Bebelstraße 40', district: 'Stuttgart-West', location: [48.7796, 9.1496] },
  {
    address: 'Willy-Brandt-Straße / Schillerstraße',
    district: 'Stuttgart-Mitte',
    location: [48.7855, 9.1878],
  },
  { address: 'Wagenburgstraße 90', district: 'Stuttgart-Ost', location: [48.7779, 9.2063] },
];
export const templates = Object.fromEntries(
  Object.entries(defaultProfiles).map(([type, id]) => [type, incidentProfiles[id]]),
) as unknown as Record<Incident['type'], { title: string; report: string }>;
export function makeIncident(
  s: SimState,
  type: Incident['type'],
  index: number,
  report?: string,
  customSite?: z.infer<typeof SiteSchema>,
  profileId: ProfileId = defaultProfiles[type],
): Incident {
  const profile = incidentProfiles[profileId];
  if (profile.type !== type) throw Error('Meldungsbild passt nicht zur Einsatzart.');
  const site = customSite ?? locations[index];
  const number = s.nextNumber++;
  return {
    id: `e-${number}`,
    number,
    type,
    title: profile.title,
    profileId,
    ...site,
    callerName:
      ['Anna', 'Lukas', 'Miriam', 'Jonas', 'Clara', 'Felix', 'Nora', 'Leon'][number % 8] +
      ' ' +
      [
        'Becker',
        'Hoffmann',
        'Wagner',
        'Schulz',
        'Fischer',
        'Weber',
        'Koch',
        'Richter',
        'Wolf',
        'Klein',
        'Schäfer',
      ][Math.floor(number / 8) % 11],
    report: report || profile.report,
    priority: profile.priority,
    phase: 'gemeldet',
    createdAt: s.now,
    escalation: 0,
    workSeconds: 0,
    actions: [],
    timeline: [{ id: `call-${number}`, at: s.now, text: report || profile.report, type: 'call' }],
  };
}
export function initialState(now = Date.now()): SimState {
  const date = new Date(now);
  date.setHours(8, 42, 0, 0);
  const stations: Station[] = [
    {
      id: 'w1',
      name: 'Feuerwache 1 · Mitte',
      service: 'Feuerwehr',
      location: [48.776, 9.173],
      level: 1,
    },
    {
      id: 'w2',
      name: 'Feuerwache 2 · West',
      service: 'Feuerwehr',
      location: [48.7737, 9.144],
      level: 1,
    },
    {
      id: 'w3',
      name: 'Rettungswache · Mitte',
      service: 'Rettungsdienst',
      location: [48.787, 9.173],
      level: 1,
    },
    {
      id: 'w4',
      name: 'Rettungswache · Ost',
      service: 'Rettungsdienst',
      location: [48.783, 9.205],
      level: 1,
    },
    {
      id: 'w5',
      name: 'Polizeirevier · Mitte',
      service: 'Polizei',
      location: [48.78, 9.178],
      level: 1,
    },
  ];
  const rows: [string, Vehicle['kind'], string][] = [
    ['Florian 1/46-1', 'HLF', 'w1'],
    ['Florian 1/33-1', 'DLK', 'w1'],
    ['Florian 2/46-1', 'HLF', 'w2'],
    ['Florian 2/46-2', 'HLF', 'w2'],
    ['Florian 2/33-1', 'DLK', 'w2'],
    ['Rotkreuz 1/83-1', 'RTW', 'w3'],
    ['Rotkreuz 1/83-2', 'RTW', 'w3'],
    ['Rotkreuz 1/82-1', 'NEF', 'w3'],
    ['Rotkreuz 2/83-1', 'RTW', 'w4'],
    ['Rotkreuz 2/82-1', 'NEF', 'w4'],
    ['Neckar 1/10', 'FuStW', 'w5'],
    ['Neckar 1/11', 'FuStW', 'w5'],
  ];
  const s: SimState = {
    version: 1,
    radioMessages: [],
    nextRadioSequence: 1,
    speechRequests: [],
    revision: 0,
    now: date.getTime(),
    nextNumber: 2401,
    seed: 72451,
    running: true,
    speed: 1,
    autoGenerate: false,
    nextCallAt: date.getTime() + 240000,
    area: structuredClone(defaultArea),
    stations,
    vehicles: rows.map(([name, kind, stationId], index) => ({
      id: `v${index + 1}`,
      name,
      kind,
      stationId,
      status: 2,
      incidentId: null,
      location: [...stations.find((w) => w.id === stationId)!.location],
      journey: null,
    })),
    incidents: [],
  };
  s.incidents = [
    makeIncident(s, 'brand', 0),
    makeIncident(s, 'vu', 1),
    makeIncident(s, 'brand', 2),
  ];
  return s;
}

export function initialPlayerState(now = Date.now()): SimState {
  const date = new Date(now);
  date.setHours(8, 0, 0, 0);
  return {
    version: 1,
    radioMessages: [],
    nextRadioSequence: 1,
    speechRequests: [],
    revision: 0,
    now: date.getTime(),
    nextNumber: 1001,
    seed: Math.floor(Math.random() * 0xffffffff),
    running: false,
    speed: 1,
    autoGenerate: true,
    nextCallAt: date.getTime() + 240000,
    area: structuredClone(defaultArea),
    stations: [],
    vehicles: [],
    incidents: [],
  };
}
